import { supabase } from '../supabaseClient';

/**
 * Serviço para gerenciar alertas climáticos
 */
export class AlertasService {
  
  /**
   * Verifica alertas de um usuário específico
   * Chama a função SQL que processa todos os alertas e cria notificações
   */
  static async verificarAlertasUsuario(userId) {
    try {
      console.log('🔔 Verificando alertas do usuário:', userId);
      
      // Chama a função SQL que verifica alertas
      const { data, error } = await supabase.rpc('verificar_alertas_climaticos');
      
      if (error) {
        console.error('❌ Erro ao verificar alertas:', error);
        return { success: false, error };
      }
      
      console.log('✅ Alertas verificados com sucesso');
      return { success: true };
      
    } catch (err) {
      console.error('❌ Erro inesperado ao verificar alertas:', err);
      return { success: false, error: err };
    }
  }

  /**
   * Verifica alertas após criar um novo
   * Útil para verificação imediata quando usuário cadastra alerta
   */
  static async verificarAposNovoAlerta(userId, municipio = null) {
    try {
      console.log('🔔 Verificando alertas após criação...', { userId, municipio });
      
      // Executa verificação de alertas
      await this.verificarAlertasUsuario(userId);
      
      // Busca notificações recentes criadas (últimos 5 minutos)
      const { data: notificacoes, error } = await supabase
        .from('notificacoes')
        .select('*')
        .eq('user_id', userId)
        .eq('tipo', 'alerta')
        .gte('criado_em', new Date(Date.now() - 5 * 60 * 1000).toISOString())
        .order('criado_em', { ascending: false });
      
      if (error) {
        console.error('Erro ao buscar notificações:', error);
        return { alertasAtivados: 0 };
      }
      
      console.log(`✅ Encontradas ${notificacoes?.length || 0} notificações de alerta recentes`);
      return { alertasAtivados: notificacoes?.length || 0, notificacoes };
      
    } catch (err) {
      console.error('Erro ao verificar alertas:', err);
      return { alertasAtivados: 0 };
    }
  }

  /**
   * Formata mensagem de alerta para exibição
   */
  static formatarMensagemAlerta(alerta, valorPrevisto, dataPrevistaStr) {
    const variaveis = {
      tmax: { nome: 'Temperatura Máxima', unidade: '°C' },
      tmean: { nome: 'Temperatura Média', unidade: '°C' },
      tmin: { nome: 'Temperatura Mínima', unidade: '°C' },
      ur: { nome: 'Umidade Relativa', unidade: '%' },
      rg: { nome: 'Radiação Global', unidade: 'MJ/m²' },
      ws2m: { nome: 'Vento', unidade: 'm/s' },
      ppt: { nome: 'Precipitação', unidade: 'mm' },
      eto_mm_day: { nome: 'Evapotranspiração', unidade: 'mm/dia' }
    };

    const condicoes = {
      'maior que': 'acima de',
      'menor que': 'abaixo de',
      'igual a': 'de'
    };

    const varInfo = variaveis[alerta.variavel] || { nome: alerta.variavel, unidade: '' };
    const condicaoTexto = condicoes[alerta.condicao] || alerta.condicao;
    
    return `📍 ${alerta.municipio}\n` +
           `${varInfo.nome}: ${valorPrevisto.toFixed(1)} ${varInfo.unidade}\n` +
           `Condição: ${condicaoTexto} ${alerta.valor} ${varInfo.unidade}\n` +
           `Data: ${dataPrevistaStr}`;
  }

  /**
   * Verifica se um alerta específico está ativo para determinado município
   */
  static async verificarAlertaMunicipio(userId, municipio, variavel, condicao, valor) {
    try {
      // Busca previsões dos próximos 7 dias para o município
      const dataInicio = new Date().toISOString().split('T')[0];
      const dataFim = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('climate_forecast_data')
        .select('date, ' + variavel)
        .eq('name_muni', municipio)
        .gte('date', dataInicio)
        .lte('date', dataFim)
        .order('date', { ascending: true });
      
      if (error || !data || data.length === 0) {
        return { ativado: false, mensagem: 'Sem dados de previsão disponíveis' };
      }
      
      // Verifica cada dia da previsão
      for (const dia of data) {
        const valorPrevisto = dia[variavel];
        
        if (valorPrevisto === null || valorPrevisto === undefined) continue;
        
        let condicaoAtingida = false;
        
        switch (condicao) {
          case 'maior que':
            condicaoAtingida = valorPrevisto > valor;
            break;
          case 'menor que':
            condicaoAtingida = valorPrevisto < valor;
            break;
          case 'igual a':
            condicaoAtingida = Math.abs(valorPrevisto - valor) < 0.1;
            break;
        }
        
        if (condicaoAtingida) {
          const dataPrevista = new Date(dia.date).toLocaleDateString('pt-BR');
          const condicaoTexto = {
            'maior que': 'acima de',
            'menor que': 'abaixo de',
            'igual a': 'de'
          }[condicao] || condicao;
          
          return {
            ativado: true,
            valorPrevisto,
            data: dia.date,
            dataFormatada: dataPrevista,
            mensagem: `Alerta ativado! Valor previsto ${condicaoTexto} ${valor} para o dia ${dataPrevista}`
          };
        }
      }
      
      return { ativado: false, mensagem: 'Condição não atingida nos próximos 7 dias' };
      
    } catch (err) {
      console.error('Erro ao verificar alerta específico:', err);
      return { ativado: false, mensagem: 'Erro ao verificar alerta' };
    }
  }
}
